import { useApi } from '@/api';
import { ChartTimeFilter, ExpandableKpiCard } from '@/components';
import { useChartFillDatasetsDates, useSparkColor } from '@/hooks';
import {
  ChartSkeleton,
  CurrencyValue,
  formatNumber,
  KpiCard,
  LineChart
} from '@blockanalitica/ui';
import { SimpleChartData } from '@blockanalitica/ui/package/types/src/components/charts/SimpleChart';
import { useCallback, useMemo, useState } from 'react';

type SLLIndexResponse = {
  total_treasury: string;
  total_treasury_change: string | null;
  historic: SimpleChartData[];
};

export default function TreasuryKpiWithChart() {
  const color = useSparkColor();
  const [chartDaysAgo, setChartDaysAgo] = useState('30');
  const [data, error] = useApi<SLLIndexResponse>({
    url: '/sparkstar/sparklend/treasury/',
    queryParams: {
      days_ago: chartDaysAgo
    }
  });

  const datasets = useMemo(
    () => [
      {
        xField: 'date',
        yField: 'total_treasury',
        borderColor: color
      }
    ],
    [color]
  );

  const chartData = useChartFillDatasetsDates(
    data?.historic,
    datasets,
    chartDaysAgo
  );
  const valueFormatter = useCallback((value: string | number) => {
    return <CurrencyValue value={value} variant="dynamic" />;
  }, []);

  if (error) {
    throw error;
  }

  if (!data) {
    return <ChartSkeleton />;
  }

  return (
    <ExpandableKpiCard
      text="Total Treasury"
      hoverBorderColor={color}
      kpiCard={
        <KpiCard
          data={{
            total_treasury: data['total_treasury'],
            total_treasury_change: data['total_treasury_change']
          }}
          field="total_treasury"
          options={{
            currency: 'USD'
          }}
          variant="nestedAsChild"
        />
      }>
      <LineChart
        data={chartData}
        datasets={datasets}
        valueFormatter={valueFormatter}
        showValuesInTooltip={true}
        tooltip={{
          valueFormatter: (value) => {
            return formatNumber(Number(value));
          },
          labelFormatter: () => 'Total',
          displayColors: false
        }}
        timeFilter={
          <ChartTimeFilter
            activeOption={chartDaysAgo}
            onChange={setChartDaysAgo}
          />
        }
      />
    </ExpandableKpiCard>
  );
}
